import { useMemo, useState, useEffect } from "react";
import { Search, ChevronDown, Medal, Trophy, Users, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SectionHeader from "@/components/ui/SectionHeader";
import { useHibridoGamesRanking, useHibridoGamesCategories } from "@/hooks/useHibridoGamesRanking";
import { SkeletonList } from "@/components/ui/skeleton";
import { normalizeInstagramProfileUrl } from "@/lib/instagram";
import { isParaibaAthlete } from "@/lib/paraibaAthletes";

interface AthleteInstagramRow {
  name: string;
  instagram: string;
}

const categoryBadge = (cat: string) => {
  const lower = cat.toLowerCase();
  if (lower.includes("master")) return "bg-amber-500/20 text-amber-300 border border-amber-500/35";
  if (lower.includes("rx")) return "bg-red-500/20 text-red-300 border border-red-500/35";
  if (lower.includes("intermediário") && lower.includes("feminino")) return "bg-rose-500/20 text-rose-200 border border-rose-500/35";
  if (lower.includes("intermediário")) return "bg-zinc-300/10 text-zinc-200 border border-zinc-400/25";
  if (lower.includes("scaled") && lower.includes("feminino")) return "bg-violet-500/20 text-violet-300 border border-violet-500/35";
  if (lower.includes("scaled")) return "bg-blue-500/20 text-blue-300 border border-blue-500/35";
  return "bg-red-900/35 text-red-100 border border-red-700/40";
};

export default function RankingSection() {
  const [searchQuery, setSearchQuery] = useState("");
  const { data: categories = [] } = useHibridoGamesCategories();
  const [selectedCategory, setSelectedCategory] = useState("");
  const [instagramMap, setInstagramMap] = useState<Record<string, string>>({});

  // Set initial category when loaded
  const activeCategory = selectedCategory || categories[0] || "Todas";

  const { data: athletes = [], isLoading } = useHibridoGamesRanking(activeCategory);

  // Fetch Instagram data from Supabase (same as Rankings.tsx)
  useEffect(() => {
    const fetchInstagrams = async () => {
      try {
        const { data } = await (supabase as any)
          .from("athletes")
          .select("name, instagram")
          .not("instagram", "is", null)
          .neq("instagram", "");

        if (data) {
          const map: Record<string, string> = {};
          (data as AthleteInstagramRow[]).forEach((d) => {
            if (d.name && d.instagram) {
              map[d.name.toLowerCase().trim()] = d.instagram;
            }
          });
          setInstagramMap(map);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchInstagrams();
  }, []);

  const filteredData = useMemo(() => {
    if (!searchQuery) return athletes;
    const q = searchQuery.toLowerCase();
    return athletes.filter(
      (a) =>
        a.athlete_name.toLowerCase().includes(q) ||
        (a.box_name?.toLowerCase() ?? "").includes(q) ||
        (a.team_name?.toLowerCase() ?? "").includes(q)
    );
  }, [athletes, searchQuery]);

  return (
    <section className="py-24 md:py-32 bg-background">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <SectionHeader
              subtitle="Federação Paraibana de Functional Fitness"
              title="Ranking Oficial da Paraíba"
              description="Classificação estadual dos atletas, com pontuação consolidada automaticamente a partir dos resultados do Híbrido Games. Cada atleta recebe individualmente a pontuação correspondente à colocação final de sua equipe."
            />
          </div>

          {/* Search and Filter Bar */}
          <div className="mb-8 flex flex-col md:flex-row items-stretch md:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="text"
                placeholder="Buscar atleta, box ou equipe..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 bg-background/80 backdrop-blur-sm border border-border rounded-xl text-sm focus:outline-none focus:border-primary transition-all duration-300"
              />
            </div>
            <div className="relative">
              <select
                value={activeCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                aria-label="Filtrar ranking por categoria"
                className="appearance-none w-full md:w-auto px-4 py-3 pr-10 bg-background/80 backdrop-blur-sm border border-border rounded-xl text-sm font-semibold focus:outline-none focus:border-primary transition-all duration-300 cursor-pointer"
              >
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" size={16} />
            </div>
          </div>

          {/* Ranking Table */}
          <div className="mb-10 flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Medal size={24} className="text-primary" />
                {activeCategory || "Ranking"}
              </h3>
              <p className="text-muted-foreground text-sm">Ranking Estadual • Temporada 2026</p>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users size={16} className="text-primary" />
              <span className="font-semibold">
                {filteredData.length} atleta{filteredData.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {isLoading ? (
            <SkeletonList count={8} variant="row" />
          ) : (
            <>
              <div className="hidden lg:block overflow-x-auto rounded-lg border border-border">
                <table className="ranking-table w-full">
                  <thead>
                    <tr className="bg-card/80">
                      <th className="w-16 text-center text-xs font-bold uppercase tracking-wider px-4 py-3 border-b border-border whitespace-nowrap">#</th>
                      <th className="text-xs font-bold uppercase tracking-wider px-4 py-3 border-b border-border whitespace-nowrap text-left">Atleta</th>
                      <th className="text-xs font-bold uppercase tracking-wider px-4 py-3 border-b border-border whitespace-nowrap text-left">Equipe</th>
                      <th className="text-xs font-bold uppercase tracking-wider px-4 py-3 border-b border-border whitespace-nowrap text-left">Box</th>
                      <th className="text-xs font-bold uppercase tracking-wider px-4 py-3 border-b border-border whitespace-nowrap text-left">Categoria</th>
                      <th className="text-center w-24 text-xs font-bold uppercase tracking-wider px-4 py-3 border-b border-border whitespace-nowrap">Vitórias</th>
                      <th className="text-center w-24 text-xs font-bold uppercase tracking-wider px-4 py-3 border-b border-border whitespace-nowrap">Pontos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.slice(0, 15).map((athlete) => {
                      const instagramUrl = normalizeInstagramProfileUrl(
                        instagramMap[athlete.athlete_name.toLowerCase().trim()]
                      );
                      const showParaibaFlag = isParaibaAthlete(athlete.athlete_name, athlete.category);
                      return (
                        <tr key={athlete.id} className="border-b border-border/50 hover:bg-card/50 transition-colors">
                          <td className="text-center px-4 py-4 whitespace-nowrap">
                            <span className="text-muted-foreground font-medium">{athlete.overall_placement}</span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white">{athlete.athlete_name}</span>
                              {showParaibaFlag && (
                                <img
                                  src="/flag-pb.svg"
                                  alt="Atleta paraibano"
                                  title="Atleta paraibano"
                                  className="h-4 w-4 rounded-[2px] object-cover flex-shrink-0"
                                />
                              )}
                              {instagramUrl && (
                                <a
                                  href={instagramUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary/60 hover:text-primary transition-colors"
                                  title="Instagram"
                                  aria-label={`Instagram de ${athlete.athlete_name}`}
                                >
                                  <Instagram size={14} aria-hidden="true" />
                                </a>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-muted-foreground whitespace-nowrap">{athlete.team_name || "—"}</td>
                          <td className="px-4 py-4 text-muted-foreground whitespace-nowrap">{athlete.box_name || "—"}</td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-sm ${categoryBadge(athlete.category)}`}>
                              {athlete.category}
                            </span>
                          </td>
                          <td className="text-center px-4 py-4 whitespace-nowrap">
                            <span className="text-sm text-muted-foreground inline-flex items-center justify-center gap-1">
                              <Trophy size={12} className="text-primary flex-shrink-0" />
                              {athlete.first_places}
                            </span>
                          </td>
                          <td className="text-center px-4 py-4 whitespace-nowrap">
                            <span className="text-lg font-bold text-primary">{athlete.total_accumulated}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lg:hidden flex flex-col gap-2 mt-2 w-full max-w-full overflow-hidden">
                {filteredData.slice(0, 10).map((athlete) => {
                  const rank = athlete.overall_placement;
                  const rankColor = rank === 1 ? 'text-yellow-500 drop-shadow-sm' :
                                   rank === 2 ? 'text-gray-300 drop-shadow-sm' :
                                   rank === 3 ? 'text-amber-600 drop-shadow-sm' :
                                   'text-muted-foreground';
                  const instagramUrl = normalizeInstagramProfileUrl(
                    instagramMap[athlete.athlete_name.toLowerCase().trim()]
                  );
                  const showParaibaFlag = isParaibaAthlete(athlete.athlete_name, athlete.category);

                  return (
                    <div
                      key={athlete.id}
                      className="group w-full max-w-full overflow-hidden flex flex-col py-3 px-3 bg-card/40 border border-border/40 rounded-xl transition-all active:scale-[0.99] hover:bg-card/60"
                    >
                      <div className="flex items-center gap-3">
                        {/* Rank Medallion */}
                        <div className={`w-8 flex-shrink-0 text-center font-black text-xl ${rankColor}`}>
                          #{rank}
                        </div>

                        {/* Athlete Focus */}
                        <div className="flex-1 min-w-0 pr-2">
                          <div className="flex items-center gap-2 mb-0.5">
                            <h4 className="text-sm font-bold text-foreground truncate">
                              {athlete.athlete_name}
                            </h4>
                            {showParaibaFlag && (
                              <img
                                src="/flag-pb.svg"
                                alt="Atleta paraibano"
                                title="Atleta paraibano"
                                className="h-3.5 w-3.5 rounded-[2px] object-cover flex-shrink-0"
                              />
                            )}
                            <span className={`inline-flex shrink-0 whitespace-nowrap px-1.5 py-0.5 text-[9px] font-bold rounded capitalize ${categoryBadge(athlete.category)}`}>
                              {athlete.category.replace('Intermediário', 'Inter.')}
                            </span>
                            {instagramUrl && (
                              <a
                                href={instagramUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-primary transition-colors"
                                aria-label={`Instagram de ${athlete.athlete_name}`}
                              >
                                <Instagram size={14} aria-hidden="true" />
                              </a>
                            )}
                          </div>

                          {(athlete.team_name || athlete.box_name) && (
                            <div className="text-[11px] text-muted-foreground truncate mb-2">
                              {athlete.team_name || athlete.box_name}
                            </div>
                          )}

                          {/* Minimalist Metrics Line */}
                          <div className="flex items-center gap-3 text-[10px] text-muted-foreground font-medium">
                            <span className="flex items-center gap-0.5">
                              <Trophy size={10} className="text-yellow-500/80 mb-0.5" />
                              <strong className="text-foreground/90 font-bold">{athlete.first_places}</strong>
                            </span>
                          </div>
                        </div>

                        {/* Total Score */}
                        <div className="flex flex-col items-end justify-center flex-shrink-0 border-l border-border/50 pl-3">
                          <span className="text-[9px] text-muted-foreground font-semibold uppercase mb-0.5">Pontos</span>
                          <span className="text-xl font-bold text-primary tracking-tight">
                            {athlete.total_accumulated}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {filteredData.length === 0 && (
                <div className="text-center py-12">
                  <Trophy className="mx-auto mb-4 size-12 text-primary/20" />
                  <p className="text-muted-foreground">
                    {searchQuery
                      ? `Nenhum atleta encontrado para "${searchQuery}"`
                      : "Nenhum atleta ranqueado nesta categoria ainda. Os pontos serão calculados automaticamente quando os scores dos eventos forem lançados."}
                  </p>
                </div>
              )}
            </>
          )}

          {/* CTA Button */}
          <div className="mt-8 flex justify-center">
            <Link
              to="/rankings"
              className="inline-flex items-center gap-2 px-8 py-3 border border-border text-foreground font-bold uppercase tracking-wider text-sm hover:border-primary hover:text-primary transition-colors"
            >
              Ver Ranking Completo
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                <polyline points="16 7 22 7 22 13"></polyline>
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
